/**
 * DeviceCheck (Sprint 6 B6) — pre-flight kiểm tra browser + webcam permission.
 *
 * Required checks:
 *   1. Fullscreen API support (`document.documentElement.requestFullscreen`)
 *   2. Webcam permission (nếu quiz.webcam_required) → getUserMedia test ngắn
 *
 * Optional info:
 *   3. Browser name + version (UA parse cơ bản)
 *
 * Props:
 *   - webcamRequired: bool — quyết định check #2 hay skip
 *   - onResult: callback (passed: bool) → parent enable/disable nút "Bắt đầu thi"
 *
 * UX: list từng check với icon ✓/✗ + button "Kiểm tra lại" nếu fail.
 */

import { useCallback, useEffect, useState } from "react";
import { Alert, Button, List, Space, Tag, Typography } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";

const { Text, Paragraph } = Typography;

type CheckStatus = "pending" | "pass" | "fail";

interface Check {
  key: string;
  label: string;
  status: CheckStatus;
  errorMessage?: string;
}

interface Props {
  webcamRequired: boolean;
  onResult?: (passed: boolean) => void;
}

export function DeviceCheck({ webcamRequired, onResult }: Props) {
  const [fullscreenCheck, setFullscreenCheck] = useState<CheckStatus>("pending");
  const [webcamCheck, setWebcamCheck] = useState<CheckStatus>(
    webcamRequired ? "pending" : "pass",
  );
  const [webcamError, setWebcamError] = useState<string>("");

  const runChecks = useCallback(async () => {
    // ─── Fullscreen API ────────────────────────────────────────────────
    const el = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
    };
    const fullscreenSupported = !!(
      el.requestFullscreen || el.webkitRequestFullscreen
    );
    setFullscreenCheck(fullscreenSupported ? "pass" : "fail");

    // ─── Webcam ─────────────────────────────────────────────────────────
    if (!webcamRequired) {
      setWebcamCheck("pass");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setWebcamCheck("fail");
      setWebcamError("Trình duyệt không hỗ trợ truy cập camera.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      // Test xong → stop ngay để LED tắt
      stream.getTracks().forEach((t) => t.stop());
      setWebcamCheck("pass");
      setWebcamError("");
    } catch (err) {
      const name = (err as Error)?.name ?? "Unknown";
      setWebcamCheck("fail");
      setWebcamError(
        name === "NotAllowedError"
          ? "Bạn đã từ chối quyền camera. Mở settings trình duyệt cho phép lại."
          : name === "NotFoundError"
            ? "Không phát hiện camera trên máy."
            : `Lỗi camera: ${name}`,
      );
    }
  }, [webcamRequired]);

  useEffect(() => {
    void runChecks();
  }, [runChecks]);

  const passed =
    fullscreenCheck === "pass" &&
    (webcamRequired ? webcamCheck === "pass" : true);

  useEffect(() => {
    if (onResult && fullscreenCheck !== "pending" && webcamCheck !== "pending") {
      onResult(passed);
    }
  }, [passed, fullscreenCheck, webcamCheck, onResult]);

  const checks: Check[] = [
    {
      key: "fullscreen",
      label: "Trình duyệt hỗ trợ chế độ toàn màn hình",
      status: fullscreenCheck,
    },
    ...(webcamRequired
      ? [
          {
            key: "webcam",
            label: "Camera đã sẵn sàng",
            status: webcamCheck,
            errorMessage: webcamError,
          },
        ]
      : []),
  ];

  const renderStatusIcon = (status: CheckStatus) => {
    if (status === "pass")
      return <CheckCircleFilled style={{ color: "#52c41a", fontSize: 20 }} />;
    if (status === "fail")
      return <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 20 }} />;
    return <SyncOutlined spin style={{ color: "#1677ff", fontSize: 20 }} />;
  };

  return (
    <div data-testid="device-check">
      <Paragraph strong>Kiểm tra thiết bị</Paragraph>
      <List
        size="small"
        bordered
        dataSource={checks}
        renderItem={(c) => (
          <List.Item>
            <Space>
              {renderStatusIcon(c.status)}
              <Text>{c.label}</Text>
              {c.status === "fail" && (
                <Tag color="error">{c.errorMessage || "Thất bại"}</Tag>
              )}
            </Space>
          </List.Item>
        )}
      />
      {!passed &&
        fullscreenCheck !== "pending" &&
        webcamCheck !== "pending" && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Một số kiểm tra chưa đạt"
            description="Bạn cần đạt tất cả check trên mới được bắt đầu thi."
            action={
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setFullscreenCheck("pending");
                  setWebcamCheck(webcamRequired ? "pending" : "pass");
                  void runChecks();
                }}
              >
                Kiểm tra lại
              </Button>
            }
          />
        )}
    </div>
  );
}
